import { useState } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { Button, Checkbox, Tag } from '@carbon/react';
import { Add, Close, Edit } from '@carbon/react/icons';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from 'components/accordion/accordion';
import { ColorSwatch, DEFAULT_COLORS } from 'components/color-picker';
import { ColorByAttributeModal } from 'components/color-by-attribute-modal';
import {
  CrossSectionRailSection,
  crossSectionArmedAtom,
} from 'components/cross-section';
import { format0 } from 'utils/number';
import {
  areaColorRulesAtom,
  areaWellFiltersAtom,
  mapConfigAtom,
  wellStatusColorsAtom,
} from './atoms';
import { AreaWellFiltersModal } from './area-well-filters-modal';
import { activeFilterCount, wellFilterLabel } from './well-filters';
import {
  AREA_COLOR_CATEGORIES,
  AREA_COLOR_NEW_RULE_APPLY_TO,
  AREA_COLOR_RESET_APPLY_TO,
  AREA_COLOR_TARGETS,
  colorCategoryLabel,
  colorTargetLabel,
} from './color-by';
import type { AreaColorFacets } from './use-area-color-facets';
import {
  SECTIONS,
  type SectionKey,
  type SectionVisibility,
} from './section-visibility';
import {
  WELL_STATUSES,
  wellsLayerOptions,
  type WellStatus,
} from './well-statuses';
import styles from './area-settings-panel.module.scss';

interface AreaSettingsPanelProps {
  visibility: SectionVisibility;
  onToggleSection: (section: SectionKey) => void;
  /** How many wells the boundary caught, for the panel's heading. */
  wellCount: number | null;
  /** The colour-by picklists, from the facet call the modal's open triggers. */
  colorFacets: AreaColorFacets;
  /** Groups the facet call's wells by the selected values, for the map paint. */
  onColorSelectionChange: (categoryId: string, values: string[]) => void;
}

// The detail page's left rail: which sections show, and what the map draws. Built on
// the shared accordion the Type Curve filters rail uses.

/**
 * A row's swatch: the colour picked for its status, else the status's own.
 * @param {WellStatus} status - The legend row.
 * @param {object} statusColors - The legend's colours by status.
 * @return {string} The swatch colour.
 */
const swatchColor = (
  status: WellStatus,
  statusColors: Record<string, string>
): string => statusColors[status.value] ?? status.color;
export const AreaSettingsPanel = ({
  visibility,
  onToggleSection,
  wellCount,
  colorFacets,
  onColorSelectionChange,
}: AreaSettingsPanelProps): JSX.Element => {
  const [mapConfig, setMapConfig] = useAtom(mapConfigAtom);
  const [statusColors, setStatusColors] = useAtom(wellStatusColorsAtom);
  const [colorRules, setColorRules] = useAtom(areaColorRulesAtom);
  const [wellFilters, setWellFilters] = useAtom(areaWellFiltersAtom);
  const [colorModalOpen, setColorModalOpen] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [filterCategory, setFilterCategory] = useState<string | null>(null);
  const crossSectionArmed = useAtomValue(crossSectionArmedAtom);
  const wells = mapConfig.wellsV2;

  // Opens the filter modal, refreshing the value lists first so they reflect the wells
  // the boundary currently catches.
  const openFilterModal = (categoryId: string | null) => {
    setFilterCategory(categoryId);
    colorFacets.refetch();
    setFilterModalOpen(true);
  };

  // Stops filtering on one attribute.
  const clearFilter = (attribute: string) =>
    setWellFilters((current) => ({ ...current, [attribute]: [] }));

  // Opens the colour modal, refreshing the value lists first so they reflect the wells
  // the boundary currently catches.
  const openColorModal = (ruleId: string | null) => {
    setEditingRuleId(ruleId);
    colorFacets.refetch();
    setColorModalOpen(true);
  };

  // Flips one rule between painting and not, leaving it in the list either way.
  const toggleRuleEnabled = (ruleId: string) =>
    setColorRules((current) =>
      current.map((rule) => {
        if (rule.ruleId !== ruleId) {
          return rule;
        }
        return { ...rule, enabled: !rule.enabled };
      })
    );

  // Drops a rule.
  const removeRule = (ruleId: string) =>
    setColorRules((current) =>
      current.filter((rule) => rule.ruleId !== ruleId)
    );

  // Recolours one of a rule's values.
  const changeValueColor = (ruleId: string, value: string, color: string) =>
    setColorRules((current) =>
      current.map((rule) => {
        if (rule.ruleId !== ruleId) {
          return rule;
        }
        return {
          ...rule,
          valueColors: { ...rule.valueColors, [value]: color },
        };
      })
    );

  /**
   * Records the colour picked for one status.
   * @param {WellStatus} status - The legend row.
   * @param {string} color - The picked colour.
   */
  const setStatusColor = (status: WellStatus, color: string) =>
    setStatusColors((current) => ({ ...current, [status.value]: color }));

  // Shows or hides the wells layer, the way the map's own selector does — every status
  // plus the bores themselves, without which nothing paints.
  const toggleWells = (checked: boolean) => {
    setMapConfig((current) => ({
      ...current,
      wellsV2: {
        checked,
        // Same builder the starting config uses, so the toggle can't write a state the
        // default wouldn't — hole directions and bore geometries ride along.
        options: { ...current.wellsV2.options, ...wellsLayerOptions(checked) },
      },
    }));
  };

  return (
    <div className={styles.panel}>
      <Accordion defaultExpanded>
        <AccordionSummary>
          <div className={styles.sectionTitle}>Layout Settings</div>
        </AccordionSummary>
        <AccordionDetails>
          <div className={styles.section}>
            <p className={styles.groupLabel}>Sections</p>
            <div className={styles.checkboxGroup}>
              {SECTIONS.map((section) => (
                <Checkbox
                  key={section.key}
                  id={`area-section-${section.key}`}
                  labelText={section.label}
                  checked={visibility[section.key]}
                  onChange={() => onToggleSection(section.key)}
                />
              ))}
            </div>
          </div>
        </AccordionDetails>
      </Accordion>

      <Accordion defaultExpanded>
        <AccordionSummary>
          <div className={styles.sectionTitle}>
            {wellCount === null ? 'Wells' : `Wells (${format0(wellCount)})`}
          </div>
        </AccordionSummary>
        <AccordionDetails>
          <div className={styles.section}>
            <div className={styles.checkboxGroup}>
              <Checkbox
                id="area-wells-on-map"
                labelText="Show wells on map"
                checked={wells.checked}
                onChange={(_event, { checked }) => toggleWells(checked)}
              />
            </div>

            {/* A colour key: the swatch says how the map paints each status. Visibility
                is the checkbox above, not per row. */}
            <p className={styles.legendTitle} id="area-well-status-label">
              Well status
            </p>
            <ul
              className={styles.legend}
              aria-labelledby="area-well-status-label"
            >
              {WELL_STATUSES.map((status) => (
                <li className={styles.legendRow} key={status.value}>
                  {/* The shared swatch, so this opens the same picker the create
                      drawer's Display Color uses. */}
                  <ColorSwatch
                    color={swatchColor(status, statusColors)}
                    onChange={(color) => setStatusColor(status, color)}
                  />
                  {status.label}
                </li>
              ))}
            </ul>

            {/* Add Filter and Color by Attribute, laid out as the Type Curve rail
                has them. */}
            <div className={styles.actions}>
              <Button
                kind="tertiary"
                size="md"
                renderIcon={Add}
                onClick={() => openFilterModal(null)}
              >
                Add Filter
              </Button>
            </div>

            {activeFilterCount(wellFilters) === 0 ? (
              <p className={styles.emptyText}>No filters have been added yet</p>
            ) : (
              <div className={styles.chips}>
                {Object.entries(wellFilters)
                  .filter(([, values]) => values.length > 0)
                  .map(([attribute, values]) => (
                    <Tag
                      key={attribute}
                      type="blue"
                      filter
                      onClick={() => openFilterModal(attribute)}
                      onClose={() => clearFilter(attribute)}
                      title={`Clear ${wellFilterLabel(attribute)}`}
                    >
                      {`${wellFilterLabel(attribute)}: ${values.length}`}
                    </Tag>
                  ))}
              </div>
            )}

            <div className={styles.actions}>
              <Button
                kind="tertiary"
                size="md"
                renderIcon={Add}
                onClick={() => openColorModal(null)}
              >
                Color by Attribute
              </Button>
            </div>

            {colorRules.length === 0 ? (
              <p className={styles.emptyText}>
                No color rules have been added yet
              </p>
            ) : (
              <div className={styles.ruleList}>
                {colorRules.map((rule) => (
                  <div className={styles.rule} key={rule.ruleId}>
                    <div className={styles.ruleHeader}>
                      <span className={styles.ruleName}>
                        {colorCategoryLabel(rule.attribute)}
                      </span>
                      <div className={styles.ruleActions}>
                        <Button
                          kind="ghost"
                          size="sm"
                          hasIconOnly
                          iconDescription="Edit rule"
                          renderIcon={Edit}
                          onClick={() => openColorModal(rule.ruleId)}
                        />
                        <Button
                          kind="ghost"
                          size="sm"
                          hasIconOnly
                          iconDescription="Remove rule"
                          renderIcon={Close}
                          onClick={() => removeRule(rule.ruleId)}
                        />
                      </div>
                    </div>

                    <div className={styles.ruleControls}>
                      {Object.entries(rule.applyTo).some(([, on]) => on) && (
                        <span className={styles.activeOn}>
                          Active on:{' '}
                          {Object.entries(rule.applyTo)
                            .filter(([, on]) => on)
                            .map(([target]) => colorTargetLabel(target))
                            .join(', ')}
                        </span>
                      )}
                      <Button
                        kind="ghost"
                        size="sm"
                        onClick={() => toggleRuleEnabled(rule.ruleId)}
                      >
                        {rule.enabled ? 'Disable' : 'Enable'}
                      </Button>
                    </div>

                    {rule.selectedValues.map((value, valueIndex) => (
                      <div className={styles.swatchRow} key={value}>
                        <ColorSwatch
                          color={
                            rule.valueColors[value] ??
                            DEFAULT_COLORS[valueIndex % DEFAULT_COLORS.length]
                          }
                          onChange={(color) =>
                            changeValueColor(rule.ruleId, value, color)
                          }
                        />
                        {rule.selectedValueLabels?.[valueIndex] ?? value}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        </AccordionDetails>
      </Accordion>

      {/* UAT: Geologic Interval for the section's grids, as the Main Map's rail has
          it. Wells stay with the Wells block above, which colours the section too. */}
      {crossSectionArmed && (
        <Accordion defaultExpanded>
          <AccordionSummary>
            <div className={styles.sectionTitle}>Cross Section</div>
          </AccordionSummary>
          <AccordionDetails>
            <div className={styles.crossSectionSection}>
              <CrossSectionRailSection
                showWells={false}
                canAddGridRules={false}
              />
            </div>
          </AccordionDetails>
        </Accordion>
      )}

      <AreaWellFiltersModal
        open={filterModalOpen}
        filters={wellFilters}
        initialCategory={filterCategory}
        getOptions={colorFacets.getOptions}
        loading={colorFacets.loading}
        error={colorFacets.error}
        onClose={() => setFilterModalOpen(false)}
        onApply={(next) => {
          setWellFilters(next);
          setFilterModalOpen(false);
        }}
      />

      <ColorByAttributeModal
        open={colorModalOpen}
        onClose={() => setColorModalOpen(false)}
        editingRuleId={editingRuleId}
        rules={colorRules}
        onApply={setColorRules}
        categories={AREA_COLOR_CATEGORIES}
        getOptions={colorFacets.getOptions}
        applyToTargets={AREA_COLOR_TARGETS}
        newRuleApplyTo={AREA_COLOR_NEW_RULE_APPLY_TO}
        resetApplyTo={AREA_COLOR_RESET_APPLY_TO}
        onSelectionChange={onColorSelectionChange}
        isFetching={colorFacets.loading}
      />
    </div>
  );
};
